import { HttpStatusCode } from 'axios'
import { useToast } from 'vuestic-ui'

import { OFFLINE_ALERT_MESSAGE, SERVER_UNREACHABLE_MESSAGE } from '../constants'
import { getApiErrorMessage } from '../utils/apiErrorMessage'
import { getSessionManager } from '../utils/sessionManager'

/** Avoid stacking identical network toasts when several requests fail at once (e.g. CSRF + login). */
let lastNetworkToastAt = 0
const NETWORK_TOAST_COOLDOWN_MS = 3000

/** Login POST path. The backend returns 401 for bad credentials, not only for an expired session. */
const AUTH_LOGIN_PATH = '/auth/login'

function toastOnceReady(message: string, color: 'danger' | 'warning' = 'danger') {
  try {
    useToast().init({ message, color })
  } catch {
    // First navigation can run before Vuestic is installed; never let that crash the app.
  }
}

function toastNetworkOnce(message: string) {
  const now = Date.now()
  if (now - lastNetworkToastAt < NETWORK_TOAST_COOLDOWN_MS) {
    return
  }
  lastNetworkToastAt = now
  toastOnceReady(message)
}

function isAuthLoginRequest(error: { config?: { url?: string } }): boolean {
  const url = error.config?.url ?? ''
  const queryStart = url.indexOf('?')
  const path = queryStart === -1 ? url : url.slice(0, queryStart)
  return path === AUTH_LOGIN_PATH || path.endsWith(AUTH_LOGIN_PATH)
}

function isLoginRoute(routeName: unknown): boolean {
  return routeName === 'login'
}

function toastExpiredSessionUnlessOnLogin() {
  // Microtask, not the login-replace macrotask: the auth guard may already have
  // landed on login by then, which would hide this copy on a stale-cookie refresh.
  // Skip only when the 401 happened while already on login (post-login /auth/user)
  // so dispatchUserLogin can own that message.
  void import('../router')
    .then(({ default: router }) => {
      if (isLoginRoute(router.currentRoute.value.name)) {
        return
      }
      toastOnceReady('Your session has expired. Please log in again.')
    })
    .catch(() => {
      // Router may not be installed yet on the first boot request.
      toastOnceReady('Your session has expired. Please log in again.')
    })
}

function redirectToLoginAfterGuardSettles() {
  // Macrotask: a synchronous replace here races the auth guard awaiting /auth/user.
  setTimeout(() => {
    void import('../router')
      .then(({ default: router }) => {
        if (isLoginRoute(router.currentRoute.value.name)) {
          return
        }
        void router.replace({ name: 'login' })
      })
      .catch(() => {
        // Router may not be installed yet on the first boot request.
      })
  }, 0)
}

function dropExpiredClientSession() {
  getSessionManager().endSession()
  void import('../store/user')
    .then(({ useUserStore }) => {
      useUserStore().clearAuthState()
    })
    .catch(() => {
      // Pinia may not be active yet on the first boot request.
    })
  toastExpiredSessionUnlessOnLogin()
  redirectToLoginAfterGuardSettles()
}

/**
 * Axios response error handler.
 *
 * Do not replace/push synchronously here. A replace during the auth guard
 * (which is already awaiting /auth/user) is what leaves the app stuck until
 * cookies are cleared. Expired-session 401s queue a login replace on a
 * macrotask so the in-flight guard can finish first.
 */
export function handleAxiosError(error: any) {
  if (
    error?.config &&
    Object.prototype.hasOwnProperty.call(error.config, 'errorHandle') &&
    error.config.errorHandle === false
  ) {
    return Promise.reject(error)
  }

  if (error?.response?.status === HttpStatusCode.Unauthorized) {
    if (isAuthLoginRequest(error)) {
      toastOnceReady(getApiErrorMessage(error, 'Invalid credentials'))
      return Promise.reject(error)
    }

    dropExpiredClientSession()
    return Promise.reject(error)
  }

  if (error?.response) {
    toastOnceReady(getApiErrorMessage(error, 'Something went wrong. Please try again.'))
    return Promise.reject(error)
  }

  toastNetworkOnce(!navigator.onLine ? OFFLINE_ALERT_MESSAGE : SERVER_UNREACHABLE_MESSAGE)
  return Promise.reject(error)
}
